package com.schoolconnect.app.feature.schoolconfig

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogButtonTextSize
import com.schoolconnect.app.designsystem.AppDialogColors
import com.schoolconnect.app.designsystem.AppDialogDefaultColors
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogFieldRow
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHelperSize
import com.schoolconnect.app.designsystem.AppDialogInputSize
import com.schoolconnect.app.designsystem.AppDialogLabelSize
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.designsystem.AppDialogSectionLabel
import com.schoolconnect.app.designsystem.AppDialogSectionTitleSize
import com.schoolconnect.app.designsystem.AppDialogSize
import com.schoolconnect.app.designsystem.AppMuted
import com.schoolconnect.app.designsystem.AppNavy
import com.schoolconnect.app.designsystem.AppPrimary
import com.schoolconnect.app.designsystem.AppSuccess
import com.schoolconnect.app.designsystem.AppText
import com.schoolconnect.app.designsystem.AppDialogHeaderSubtitleSize
import com.schoolconnect.app.designsystem.AppDialogHeaderTitleSize
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.close_x
import org.jetbrains.compose.resources.painterResource

private enum class ViewTab { General, Calendar, Features }

private val SchoolConfigViewDialogColors = AppDialogColors(
    primary = AppPrimary,
    inputBorder = AppPrimary,
    navy = AppNavy
)

@Composable
internal fun SchoolConfigViewModal(
    row: SchoolConfigRow,
    config: SchoolConfigEditData?,
    onClose: () -> Unit
) {
    val isLoadingConfig = config == null
    var tab by remember(row.id) { mutableStateOf(ViewTab.General) }

    AppDialogScaffold(
        onDismiss = onClose,
        colors = SchoolConfigViewDialogColors,
        size = AppDialogSize.Wide,
        dialogWidthOverride = 1120.dp,
        maxDialogHeight = 920.dp,
        fillDialogHeight = true,
        dismissOnClickOutside = !isLoadingConfig,
        isLoading = isLoadingConfig,
        loadingTitle = "Loading configuration"
    ) {
        SchoolConfigViewHeader(row = row, onClose = onClose)
        AppDialogDivider(colors = SchoolConfigViewDialogColors)
        AppDialogBody(
            horizontalPadding = 24.dp,
            verticalPadding = 12.dp,
            colors = SchoolConfigViewDialogColors,
            fillAvailable = true,
            showVerticalScrollbar = true
        ) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                AppDialogFieldRow(spacing = 16.dp) {
                    SchoolConfigViewField(
                        label = "School Name",
                        value = row.schoolName,
                        modifier = Modifier.weight(1f)
                    )
                    SchoolConfigViewField(
                        label = "Academic Year",
                        value = config?.academicYear.orEmpty(),
                        modifier = Modifier.weight(1f)
                    )
                }
                Column(modifier = Modifier.fillMaxWidth()) {
                    Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(4.dp)) {
                        ViewTabButton("General", tab == ViewTab.General) { tab = ViewTab.General }
                        ViewTabButton("Calendar", tab == ViewTab.Calendar) { tab = ViewTab.Calendar }
                        ViewTabButton("Features", tab == ViewTab.Features) { tab = ViewTab.Features }
                    }
                    HorizontalDivider(color = SchoolConfigViewDialogColors.border)
                }
                if (config != null) {
                    when (tab) {
                        ViewTab.General -> ViewGeneralContent(config.attendance, config.exams)
                        ViewTab.Calendar -> ViewCalendarContent(config.weeklyOffDay, config.holidays)
                        ViewTab.Features -> ViewFeaturesContent(config.features)
                    }
                }
            }
        }
        AppDialogDivider(colors = SchoolConfigViewDialogColors)
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    label = "Close",
                    onClick = onClose,
                    style = AppDialogActionStyle.Primary,
                    width = 120.dp
                )
            ),
            colors = SchoolConfigViewDialogColors,
            contentPadding = PaddingValues(horizontal = 24.dp)
        )
    }
}

@Composable
private fun SchoolConfigViewHeader(
    row: SchoolConfigRow,
    onClose: () -> Unit
) {
    val updatedOn = row.updatedDate.takeIf { it.isNotBlank() && it != "-" }.orEmpty().ifBlank { "—" }
    val auditText = buildAnnotatedString {
        val labelStyle = SpanStyle(fontWeight = FontWeight.SemiBold, color = AppMuted)
        val valueStyle = SpanStyle(fontWeight = FontWeight.Normal, color = AppMuted)
        withStyle(labelStyle) { append("Created by") }
        withStyle(valueStyle) { append(" — · — | ") }
        withStyle(labelStyle) { append("Last modified by") }
        withStyle(valueStyle) { append(" — · $updatedOn") }
    }

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(start = 24.dp, end = 20.dp, top = 16.dp, bottom = 12.dp),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.Top
    ) {
        Column(
            modifier = Modifier
                .weight(1f)
                .padding(end = 16.dp),
            verticalArrangement = Arrangement.spacedBy(4.dp)
        ) {
            Text(
                text = "View School Configuration",
                color = AppPrimary,
                fontSize = AppDialogHeaderTitleSize,
                fontWeight = FontWeight.Bold,
                maxLines = 1
            )
            Text(
                text = "School configuration details for ${row.schoolName}.",
                color = AppMuted,
                fontSize = AppDialogHeaderSubtitleSize,
                fontWeight = FontWeight.Medium,
                maxLines = 2
            )
            Text(
                text = auditText,
                fontSize = AppDialogHelperSize,
                lineHeight = 18.sp
            )
        }
        Box(
            modifier = Modifier
                .size(32.dp)
                .clickable(onClick = onClose),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                painter = painterResource(Res.drawable.close_x),
                contentDescription = "Close",
                tint = AppMuted,
                modifier = Modifier.size(20.dp)
            )
        }
    }
}

@Composable
private fun ViewTabButton(label: String, selected: Boolean, onClick: () -> Unit) {
    val interactionSource = remember { MutableInteractionSource() }
    Column(
        Modifier.width(108.dp).clickable(
            interactionSource = interactionSource,
            indication = null,
            onClick = onClick
        ),
        horizontalAlignment = Alignment.CenterHorizontally
    ) {
        Text(
            label,
            color = if (selected) AppPrimary else AppNavy,
            fontSize = AppDialogButtonTextSize,
            fontWeight = if (selected) FontWeight.SemiBold else FontWeight.Medium,
            modifier = Modifier.padding(top = 10.dp, bottom = 8.dp)
        )
        Box(Modifier.fillMaxWidth().height(2.dp).background(if (selected) AppPrimary else Color.Transparent))
    }
}

@Composable
private fun ViewGeneralContent(attendance: List<ThresholdDraft>, exams: List<ThresholdDraft>) {
    Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
        AppText("Attendance and examination performance thresholds.", color = AppNavy)
        ViewThresholdSection(
            title = "Attendance Performance Thresholds",
            rows = attendance,
            hasGrade = false
        )
        ViewThresholdSection(
            title = "Exam Performance & Grade Thresholds",
            rows = exams,
            hasGrade = true
        )
    }
}

@Composable
private fun ViewThresholdSection(
    title: String,
    rows: List<ThresholdDraft>,
    hasGrade: Boolean
) {
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(10.dp))
            .padding(10.dp),
        verticalArrangement = Arrangement.spacedBy(8.dp)
    ) {
        AppDialogSectionLabel(title, fontSize = AppDialogSectionTitleSize)
        HorizontalDivider(color = AppDialogDefaultColors.border)
        if (rows.isEmpty()) {
            Text("No thresholds configured.", color = AppMuted, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
        } else {
            Row(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Text(
                    if (hasGrade) "Performance Level" else "Attendance Level",
                    color = AppNavy,
                    fontSize = AppDialogLabelSize,
                    fontWeight = FontWeight.SemiBold,
                    modifier = Modifier.weight(1.3f)
                )
                if (hasGrade) {
                    Text("Grade", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(.7f))
                }
                Text("Minimum %", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
                Text("Maximum %", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1f))
            }
            rows.forEach { row ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(row.level.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal, modifier = Modifier.weight(1.3f))
                    if (hasGrade) {
                        Text(row.grade.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal, modifier = Modifier.weight(.7f))
                    }
                    Text(row.minimum.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal, modifier = Modifier.weight(1f))
                    Text(row.maximum.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal, modifier = Modifier.weight(1f))
                }
            }
        }
    }
}

@Composable
private fun ViewCalendarContent(weeklyOffDay: String, holidays: List<HolidayDraft>) {
    Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(10.dp))
                .padding(14.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            AppDialogSectionLabel("School Holidays", fontSize = AppDialogSectionTitleSize)
            HorizontalDivider(color = AppDialogDefaultColors.border)
            SchoolConfigViewField(label = "Weekly Off Days", value = weeklyOffDay)
            if (holidays.isEmpty()) {
                Text("No holidays configured.", color = AppMuted, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
            } else {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    Text("Date", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(.8f))
                    Text("Holiday Name", color = AppNavy, fontSize = AppDialogLabelSize, fontWeight = FontWeight.SemiBold, modifier = Modifier.weight(1.5f))
                }
                holidays.forEach { holiday ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(12.dp)
                    ) {
                        Text(holiday.date.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal, modifier = Modifier.weight(.8f))
                        Text(holiday.name.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal, modifier = Modifier.weight(1.5f))
                    }
                }
            }
        }
    }
}

@Composable
private fun ViewFeaturesContent(features: List<FeatureDraft>) {
    Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
        AppText("Optional features enabled for this school.", color = AppNavy)
        if (features.isEmpty()) {
            Text("No feature configurations.", color = AppMuted, fontSize = AppDialogHelperSize, fontWeight = FontWeight.Medium)
        } else {
            features.forEach { feature ->
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .border(1.dp, AppDialogDefaultColors.border, RoundedCornerShape(8.dp))
                        .padding(10.dp),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(feature.key.ifBlank { "—" }, color = AppNavy, fontSize = AppDialogInputSize, fontWeight = FontWeight.Normal)
                    Text(
                        text = if (feature.enabled) "Enabled" else "Disabled",
                        color = if (feature.enabled) AppSuccess else AppMuted,
                        fontSize = AppDialogLabelSize,
                        fontWeight = FontWeight.SemiBold
                    )
                }
            }
        }
    }
}

@Composable
private fun SchoolConfigViewField(
    label: String,
    value: String,
    modifier: Modifier = Modifier
) {
    Column(
        modifier = modifier.fillMaxWidth(),
        verticalArrangement = Arrangement.spacedBy(4.dp)
    ) {
        Text(
            text = label,
            color = AppNavy,
            fontSize = AppDialogLabelSize,
            fontWeight = FontWeight.SemiBold
        )
        Text(
            text = value.ifBlank { "—" },
            color = AppNavy,
            fontSize = AppDialogInputSize,
            fontWeight = FontWeight.Normal
        )
    }
}
